import { ApiService } from './api.service';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  firstValueFrom,
  Subject,
  Observable
} from 'rxjs';
import { Amplify } from '@aws-amplify/core';
import { Auth } from '@aws-amplify/auth';
import { environment } from '../environments/environment';
import { Router } from '@angular/router';
import {
  AdminAddUserToGroupCommand,
  AdminDisableUserCommand,
  AdminEnableUserCommand,
  AdminUpdateUserAttributesCommand,
  CognitoIdentityProviderClient,
  ListUsersCommand,
  ListUsersCommandInput,
  ListUsersCommandOutput,
} from '@aws-sdk/client-cognito-identity-provider';
import * as qrcode from 'qrcode';

//User Object Declaration
export interface IUser {
  email: string;
  password: string;
  code: string;
  name: string;
}

export interface SYUSER {
  userid: string | null;      // Assuming 'userid' can be a number or null
  cognitoid: string;          // 'cognitoid' is a string
  ulog: number;               // 'ulog' is a number, 1 by default
  ulock: number;              // 'ulock' is a number, 0 by default
  lastlogindate: string;      // 'lastlogindate' is a string (date format)
  lastlogintime: string;      // 'lastlogintime' is a string (time format)
  sqltrace: number;           // 'sqltrace' is a number, 0 by default
  sectype: string;            // 'sectype' is a string, likely a single character
  firstname: string;          // 'firstname' is a string
  lastname: string;           // 'lastname' is a string
  email: string;              // 'email' is a string
  phone: string;              // 'phone' is a string
  sysuser: string;            // 'sysuser' is a string
  eid: string;                // 'eid' is a string
  langid: string;             // 'langid' is a string
  uadmin: string;             // 'uadmin' is a string (likely a char)
  utype: string;              // 'utype' is a string
  certif: string;             // 'certif' is a string
  upol: number;               // 'upol' is a number, 0 by default
  polacd: string;             // 'polacd' is a string
  polact: string;             // 'polact' is a string
  acknowledge: string;        // 'acknowledge' is a string (likely a char)
  location: string;           // 'location' is a string
  syadmin: string;            // 'syadmin' is a string, 'F' by default
}


export interface CognitoUser {
  username: string;
  given_name: string;
  family_name: string;
  legal_name?: string;
  email: string;
  phone_number: string;
  address: string;
  custom_user_type: string;
  created: string;
  modified: string;
  user_status: string;
  sub: string;
  enabled: string;
  syuser?: SYUSER;
  email_verified?: string
}

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  private readonly cognitoRegion = environment.cognito.region;
  public tempEmail: string = '';
  // for general use
  private successMessage: any;
  private userSub: any;
  public userInfos: any;

  public allUserRolesDeleted: boolean = false;
  public clientUserUpdated: boolean = false;
  public temporarySub: any;
  public temporaryRole: any;
  public muirwoodUserCreated: boolean = false;
  public clientUserCreated: boolean = false;
  public distributorUserCreated: boolean = false;
  public userRoleArray: any[] = [];
  public cognitoUsersArray: CognitoUser[] = [];
  public originalCognitoUsersArray: CognitoUser[] = [];
  public temporaryArray: any[] = [];
  public clientLegalName: any;
  public distributorLegalname: any;
  public operatorLegalName: string = '';
  public legalName: string = '';

  public challengeName: string = '';
  public currentRole: string = '';

  // Variables used in MFA sing-in sign-up functions
  public imgUrl: any;
  public userName: string = '';
  public userMFAPreference: string = '';
  public qrCodeImgActive: boolean = false;
  public confirmMFAMessage: string = '';
  public challengeParamDestynation: string = '';
  public userMFA: any;

  // Fucntions that will trigger function in other components
  private wrongMFACode = new Subject<void>();
  wrongMFACode$ = this.wrongMFACode.asObservable();
  private MFASignUpSuccess = new Subject<void>();
  MFASignUpSuccess$ = this.MFASignUpSuccess.asObservable();
  private needEmailVerified = new Subject<void>();
  needEmailVerified$: Observable<void> = this.needEmailVerified.asObservable();

  public userRolesForUpdate: any;
  public userAllowed: boolean = false;

  // Variables used to get the user type
  public userType: string = '';
  public userRoles: any[] = [];

  // Variables used to get the client or distributor or operator ID
  public clientId: string = '';
  public distributorId: string = '';
  public operatorId: string = '';

  // Array used to have all client and distributor info's
  public distributorsInfosArray: any;
  public clientsInfosArray: any;
  public operatorsInfosArray: any;

  private userObject: any;
  private oldPassword: string = '';
  public emailValidationResult: String = '';
  public phoneNumberValidationResult: string = '';

  public deviceUpdateEmail: number = 0;
  public deviceUpdateSms: number = 0;
  public workOrderUpdateEmail: number = 0;
  public workOrderUpdateSms: number = 0;
  public monthlyReportEmail: number = 0;
  public monthlyReportSms: number = 0;
  public alertPreferencesResult: any[] = [];
  public preferedLanguage: string = '';

  public needToVierifyEmail: boolean = false;

  public syusers: SYUSER[] = [];

  constructor(
    private router: Router,
    private api: ApiService
  ) {
    //Set configs for communicating with Cognito, Most of this will be changed when this app is hosted
    Amplify.configure({
      Auth: environment.cognito,
    });
  }

  private storeSessionCredentials(credentials: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
    identityId?: string;
  }) {
    sessionStorage.setItem('accessKeyId', credentials.accessKeyId);
    sessionStorage.setItem('secretAccessKey', credentials.secretAccessKey);
    if (credentials.sessionToken) {
      sessionStorage.setItem('sessionToken', credentials.sessionToken);
    }
    if (credentials.identityId) {
      sessionStorage.setItem('cognitoUserIdentityId', credentials.identityId);
    }
  }

  private getSessionCredentials():
    | {
        accessKeyId: string;
        secretAccessKey: string;
        sessionToken?: string;
      }
    | undefined {
    const accessKeyId = sessionStorage.getItem('accessKeyId') || '';
    const secretAccessKey = sessionStorage.getItem('secretAccessKey') || '';
    const sessionToken = sessionStorage.getItem('sessionToken') || '';

    if (!accessKeyId || !secretAccessKey) {
      return undefined;
    }

    return {
      accessKeyId,
      secretAccessKey,
      sessionToken: sessionToken || undefined,
    };
  }

  private getCognitoClient(credentials?: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }) {
    const resolvedCredentials = credentials ?? this.getSessionCredentials();
    return new CognitoIdentityProviderClient({
      region: this.cognitoRegion,
      credentials: resolvedCredentials,
    });
  }

  async configure() {
    Amplify.configure({
      cognito: environment.cognito,
    });
    //Returns temporary credentials for authenticated users
    const credentials = await Auth.currentCredentials();
    this.storeSessionCredentials(credentials);
  }

  //Fetch authenticated user's temporary credential and set Session variables
  public async getKeys(errorCode: string = '') {
    //Returns temporary credentials for authenticated users
    const credentials = await Auth.currentCredentials();

    //Set Session variables
    this.storeSessionCredentials(credentials);

    //Return credentials in an Object for use (if necessary)
    return {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    };
  }

  // verify if will be used at nova login work flow
  async refreshToken() {
    try {
      //Refresh the session tokens if refresh token is still valid
      await Auth.currentAuthenticatedUser({ bypassCache: true });
    } catch (error) {
      this.checkForbidden(error);
      console.error('Error refreshing token:', error);
      // Handle the error, e.g., redirect to login or re-authenticate the user
      Auth.signOut()
        .then((res) => {
          this.router.navigate(['/signout']);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  //Verify the Authenticated User Session. Force sign out when authentication expired.
  async verifySession() {
    //Are session keys set?
    if (
      sessionStorage.getItem('sessionToken') === undefined ||
      sessionStorage.getItem('accessKeyId') === undefined ||
      sessionStorage.getItem('secretAccessKey') === undefined
    ) {
      //Get and set keys is session variables have not been set.
      await this.getKeys();
      //console.log('trying session refresh again')
      window.location.reload();
    }

    try {
      const session = await Auth.currentSession();
      const expirationTime = session.getIdToken().getExpiration();

      // Check if the token is expired or close to expiration
      const currentTime = Math.floor(Date.now() / 1000);

      const timeUntilExpiration = expirationTime - currentTime;

      if (timeUntilExpiration < 6000) {
        // Token is about to expire, initiate refresh
        await this.refreshToken();
        const credentials = await Auth.currentCredentials();

        //Set Session variables
        sessionStorage.setItem('accessKeyId', credentials.accessKeyId);
        sessionStorage.setItem('secretAccessKey', credentials.secretAccessKey);
        sessionStorage.setItem('sessionToken', credentials.sessionToken);
        sessionStorage.setItem('cognitoUserIdentityId', credentials.identityId);
        this.storeSessionCredentials(credentials);

        if (window.location.pathname === '/login') {
          Auth.signOut()
            .then((res) => {
              this.router.navigate(['/login']);
            })
            .catch((error) => {
              console.error(error);
            });
        } else {
          //reload page to apply restored session
          window.location.reload();
        }
      }
    } catch (error) {
      //   this.checkForbidden(error);
      //If an error occurs, force signOut()
      Auth.signOut()
        .then((res) => {})
        .catch((error) => {
          console.error(error);
          this.router.navigate(['/login']);
        });
    }
  }

  async getDBUsernameByEmail(email: string): Promise<string>{
    // Will get the SYUSER table from the BD only if this.syusers is not already implemented
    if(this.syusers.length === 0){
      this.syusers = await this.api.getSYUSERS().then((res)=>{
        return res;
      });
    }

    const user = this.syusers.find((user: any) => user.email === email);
    let userid = '';
    if(user && user.userid){
      userid =  user.userid.toString();
    }
    return userid;
  }

  /////// **********  Function used in the manage tool page *************** ////////////
  // Function used to search for users when user click on search button
  async searchUser(sortBy: string, searchField: string = ''){
    this.configure();

    // Will get the SYUSER table from the BD only if this.syusers is not already implemented
    if(this.syusers.length === 0){
      this.syusers = await this.api.getSYUSERS().then((res)=>{
        return res;
      });
    }

    // Set new instance of cognito with the proper region
    const cognitoIdentityServiceProvider = this.getCognitoClient();

    // Initialize the PaginationToken as undefined and a variable filter
    let paginationToken: string | undefined = undefined;
    let filter: string = '';

    // Set the search by in cognito parameters
    if(searchField !== ''){
      // If user set a search by and put something in the search field Ex: searchby -> username : searchfield -> JCORMIER
      filter = sortBy + " ^= \"" + searchField + "\"";
    }else{
      // If user doesn't put anything in the searchfiled put search by status Ex: search by -> confirm status
      filter = "cognito:user_status = \"" + sortBy + "\"";
    }

    const params: ListUsersCommandInput = {
      UserPoolId: environment.cognito.userPoolId,
      AttributesToGet: [
        "given_name",
        "family_name",
        "email",
        "sub",
        "email_verified"
      ],
      PaginationToken: paginationToken,
      Filter: JSON.parse(JSON.stringify(filter))
    }

    do{
      const result = await this.listUserWithRetry(params, cognitoIdentityServiceProvider);
      if (!result) {
        return;
      }

      const users = result.Users || [];
      paginationToken = result.PaginationToken;
      params.PaginationToken = paginationToken;

      users.forEach((user: any) => {
        const findAttribute = (name: string) => {
          const attr = user.Attributes?.find((a: any) => a.Name === name);
          return attr ? attr.Value : '';
        };

        const cognitoUser: CognitoUser = {
          username: user.Username || '',
          given_name: findAttribute('given_name') || '',
          family_name: findAttribute('family_name') || '',
          email: findAttribute('email') || '',
          phone_number: findAttribute('phone_number') || '',
          address: findAttribute('address') || '',
          custom_user_type: findAttribute('custom:user_type')?.toUpperCase() || '',
          created: user.UserCreateDate?.toString() || '',
          modified: user.UserLastModifiedDate?.toString() || '',
          user_status: user.UserStatus || '',
          sub: findAttribute('sub'),
          enabled: findAttribute('custom:enable') || '',
          syuser: this.syusers.find((dbUser: any) => dbUser.userid?.toUpperCase().trim() === user.Username?.toUpperCase().trim()),
          email_verified: findAttribute('email_verified')
        };

        this.cognitoUsersArray.push(cognitoUser);
      });
    }while(paginationToken);
  }

  async listUserWithRetry(
    params: ListUsersCommandInput,
    cognitoServiceProvider: CognitoIdentityProviderClient
  ): Promise<ListUsersCommandOutput | undefined> {
    try{
      return await cognitoServiceProvider.send(new ListUsersCommand(params));
    }catch(error){
      if (
        (error as { name?: string; code?: string })?.name ===
          'ExpiredTokenException' ||
        (error as { name?: string; code?: string })?.code ===
          'ExpiredTokenException'
      ) {
        console.warn('Token expired, attempting to refresh...');
        await this.refreshToken();
        const credentials = await Auth.currentCredentials();
        this.storeSessionCredentials(credentials);
        const retryClient = this.getCognitoClient({
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        });
        return await this.listUserWithRetry(params, retryClient);
      }
      console.error('Error: ', error);
      return undefined;
    }
  }

  //Check if a user is logged in
  public async confirmValidUser() {
    try {
      await Auth.currentCredentials();
      await Auth.currentAuthenticatedUser();
      //Attempt to fetch active user information
      if (await this.getUser()) {
        //Verify the authenticated user session. Force SignOut if expired.
        this.verifySession();

        return true;
      } else {
        //Force Signout if getUser fails
        this.signOut()
          .then((response) => {
            this.router.navigate(['/login']);
          })
          .catch((error) => {
            console.error(error);
            this.router.navigate(['/login']);
          });
        //If no active user, sends the user back to /login

        return false;
      }
    } catch (error) {
      //In the event of an error, default to /login page and force SignOut
      this.checkForbidden(error);
      console.error('Error: ', error);
      this.signOut()
        .then((response) => {
          this.router.navigate(['/login']);
        })
        .catch((error) => {
          console.error(error);
          this.router.navigate(['/login']);
        });
      return false;
    }
  }

  //Returns active user information. This is how we get the User ID
  public async getUser(): Promise<any> {
    // Set a variable of user infos to avoid multiple calls
    this.userInfos = await Auth.currentUserInfo();
    return this.userInfos;
  }

  //Used for debugging sessions. Only logs session information and errors
  public async currentSession(): Promise<any> {
    // Retrieve the current session
    await Auth.currentSession()
      .then((currentSession: any) => {
        // Extract tokens from the resolved session object
        const idToken = currentSession.getIdToken().getJwtToken();
        const accessToken = currentSession.getAccessToken().getJwtToken();
        const refreshToken = currentSession.getRefreshToken().getToken();
      })
      .catch((error) => {
        this.checkForbidden(error);
        //Log errors
        console.error('Error getting session:', error);
        this.verifySession();
      });
  }

  //Sends an email to the user containing a passwor reset code
  public async forgotPassword(username: string): Promise<any> {
    try {
      //Request to AWS via Amplify to send a user a reset code by email or sms
      const forgotPassword = await Auth.forgotPassword(username);
      return forgotPassword;
    } catch (error) {
      this.checkForbidden(error);
      console.error('Something went wrong: ', error);
      return false;
    }
  }

  ///////////////// ************ Function not used yet but can be used to disabled a user access directly from the login portal *************** ////////////////////
  // Function called to disable user access in cognito
  async disableUserAccess(username: string): Promise<string> {
    await this.configure();
    // Initialize Cognito Identity Service Provider with the appropriate region
    const cognitoIdentityServiceProvider = this.getCognitoClient();

    // Set parameters for the user deletion request
    const params = {
      UserPoolId: environment.cognito.userPoolId,
      Username: username,
    };

    // Create a promise to handle the asynchronous operation
    return new Promise(async (resolve, reject) => {
      // Make an adminDeleteUser request to delete the specified user
      try {
        await cognitoIdentityServiceProvider.send(
          new AdminDisableUserCommand(params)
        );
        // If we are able to disable user in cognito we need to set his attribute enable to '0' so we know he's disabled
        const updateParams = {
          UserPoolId: environment.cognito.userPoolId,
          Username: username,
          UserAttributes: [
            {
              Name: 'custom:enable',
              Value: '0',
            },
          ],
        };
        await cognitoIdentityServiceProvider.send(
          new AdminUpdateUserAttributesCommand(updateParams)
        );
        // Resolve the promise if the user is disabled successfully
        resolve('success');
      } catch (error) {
        this.checkForbidden(error);
        console.error('Error disable user:', error);
        reject(error);
      }
    });
  }

  ///////////////// ************ Function not used yet but can be used to enabled a user access directly from the login portal *************** ////////////////////
  // Function called to enabled user access in cognito
  enableUserAcces(username: string): Promise<string> {
    // Initialize Cognito Identity Service Provider with the appropriate region
    const cognitoIdentityServiceProvider = this.getCognitoClient();

    // Set parameters for the user deletion request
    const params = {
      UserPoolId: environment.cognito.userPoolId,
      Username: username,
    };

    // Create a promise to handle the asynchronous operation
    return new Promise(async (resolve, reject) => {
      // Make an adminDeleteUser request to delete the specified user
      try {
        await cognitoIdentityServiceProvider.send(
          new AdminEnableUserCommand(params)
        );
        // If we are able to enable user in cognito we need to set his attribute enable to '1' so we know he's enabled
        const updateParams = {
          UserPoolId: environment.cognito.userPoolId,
          Username: username,
          UserAttributes: [
            {
              Name: 'custom:enable',
              Value: '1',
            },
          ],
        };
        await cognitoIdentityServiceProvider.send(
          new AdminUpdateUserAttributesCommand(updateParams)
        );
        // Resolve the promise if the user is enabled successfully
        resolve('success');
      } catch (error) {
        this.checkForbidden(error);
        console.error('Error deleting user:', error);
        reject(error);
      }
    });
  }

  //Resets password as part of the forgot-password workflow. requires a reset code emailed to the user via forgotPassword()
  public async resetPassword(
    username: string,
    code: string,
    newPassword: string
  ): Promise<any> {
    try {
      const passwordReset = Auth.forgotPasswordSubmit(
        username,
        code,
        newPassword
      );
      return passwordReset;
    } catch (error) {
      this.checkForbidden(error);
      console.error('Error: ', error);
      return false;
    }
  }

  public async signIn(user: IUser): Promise<any> {
    try {
      //Sign In with AWS Cognito using AWS Amplify
      const userObject = await Auth.signIn(user.email, user.password, {
        // Specify the token scopes you need
        scopes: 'openid email profile aws.cognito.signin.user.admin',
      }).then((user) => {
        //console.log(user)
        return user;
      }).catch(async (error) => {
        // When sign-in error is NotAuthorizedException check if the email is verified
        if(error.code === 'NotAuthorizedException'){
          const response = await this.api.checkUserEmail(user.email);

          if(response === false){
            this.needEmailVerified.next();
            this.needToVierifyEmail = true;
          }
        }
      });
      this.userName = userObject.username;
      // Set is own property to be abble to reconnect whit the userObject at the first signIn of the user in changePassword function
      this.userObject = userObject;

      //If Sign In was successful, check the response from AWS
      if (userObject) {
        switch (userObject.challengeName) {
          case 'NEW_PASSWORD_REQUIRED':
            //Handle first time sign-in with temporary passwords
            //Set global challengeName(AWS Response message)
            this.challengeName = userObject.challengeName;
            this.oldPassword = user.password;

            //Return string for conditional logic
            return 'change-password';
            break;

          case 'SMS_MFA':
          case 'SOFTWARE_TOKEN_MFA':
            this.challengeName = userObject.challengeName;
            return 'MFA-required';
            break;

          default:
            //If the Sign-In response was standard, try to fetch the current session
            try {
              const currentSession = await Auth.currentSession().then(
                (session) => {
                  // Use the user session to get temporary AWS credentials
                  this.getKeys();
                }
              );

              return userObject; // Sign-in successful
            } catch (error) {
              //Log Session Error
              this.checkForbidden(error);
              console.error('Error getting session:', error);
              return false;
            }
            break;
        }
      } else {
        return false;
      }
    } catch (error) {

      return false; // Sign-in failed
    }
  }

  //Signout to end Active Session
  public async signOut(): Promise<any> {
    return await Auth.signOut().then(() => {
    });
  }

  //Handles all password changes functionality
  public async changePassword(user: IUser, newPassword: string): Promise<any> {
    try {
      //Handle first-time login
      if (this.challengeName === 'NEW_PASSWORD_REQUIRED') {
        const newPasswordResult = await Auth.completeNewPassword(
          this.userObject,
          newPassword
        )
          .then(async (result) => {
            if (result) {
              // Set the instance of the user
              const user = await Auth.currentAuthenticatedUser();
              // Send a SMS to the phone number of the user that is provide by cognito
              await Auth.verifyUserAttribute(user, 'email');
            }
          })
          .catch((error) => {
            // this.checkForbidden(error);
            console.error('Error: ', error);
          });
      } else {
        //Standard password change for an active and authenticated user
        // Get the currently authenticated user
        await Auth.currentAuthenticatedUser()
          .then((userObj) => {
            // Change user password
            Auth.changePassword(userObj, user.password, newPassword)
              .then(() => {
                return true;
              })
              .catch((error) => {
                // this.checkForbidden(error);
                console.error('Error : ', error);
                return false;
              });
          })
          .catch((error) => {
            // this.checkForbidden(error);
            console.error('Error : ', error);
            return false;
          });
      }

      //Empty challengeName to stabilize conditional logic for AWS responses
      this.challengeName = '';

      return true;
    } catch (error) {
      //Log Error and return false to handle errors in the relevant component
      this.checkForbidden(error);
      console.error('Error in cognito service:', error);
      return false;
    }
  }

  // Function that verify the code send by email to the user
  async validateEmail(confirmationCode: string) {
    // Set the instance of the current user
    const user = await Auth.currentAuthenticatedUser();

    this.tempEmail = user.attributes.email;
    // Call the function that check the code provide by cognito and send to the user by email
    await Auth.verifyUserAttributeSubmit(user, 'email', confirmationCode)
      .then((result) => {
        console.log('Email validation result: ', result);
        this.emailValidationResult = result;
      })
      .catch((error) => {
        console.error(error);
      });
  }

  // Function that verify the code send by phone number to the user
  async validatePhoneNumber(confirmationCode: string) {
    // Set the instance of the current user
    const user = await Auth.currentAuthenticatedUser();
    // Call the function that check the code provide by cognito and send to the user by phone number
    await Auth.verifyUserAttributeSubmit(user, 'phone_number', confirmationCode)
      .then((result) => {
        this.phoneNumberValidationResult = result;
      })
      .catch((error) => {
        this.checkForbidden(error);
        console.error(error);
      });
  }

  // Function called to sign-in when MFA is activate on cognito user
  async MFASignIn(user: IUser, MFACode: string) {
    try {
      //Sign In with AWS Cognito using AWS Amplify
      const userObject = await Auth.signIn(user.email, user.password, {
        // Specify the token scopes you need
        scopes: 'openid email profile aws.cognito.signin.user.admin',
      });
      // Will make the confirm sign-in if SMS MFA is active for the user
      if (this.challengeName === 'SMS_MFA') {
        // Await confirmation from cognito if user enter the good MFA code
        const newUser = await Auth.confirmSignIn(
          userObject,
          MFACode,
          'SMS_MFA'
        ).then((result) => {});
      }
      // Will make the confirm sign-in if TOTP MFA is active for the user
      if (this.challengeName === 'SOFTWARE_TOKEN_MFA') {
        // Await confirmation from cognito if user enter the good MFA code
        await Auth.confirmSignIn(userObject, MFACode, 'SOFTWARE_TOKEN_MFA')
          .then((confirmation) => {
            // Return the confirmation if so
            return confirmation;
          })
          .catch((error) => {
            this.wrongMFACode.next();
            console.error(error);
            return 'no-MFA-sign-in';
          });
      }
      return '';
    } catch (error: any) {
      this.checkForbidden(error);
      console.error('Error on verification of MFA code :', error);
      return 'no-MFA-sign-in';
    }
  }

  // Function called when user want to sign-up whit SMS MFA code /////////// Not in use for now since no SMS was send from cognito
  async signUpMFACodeSMS(codeMFA: string) {
    try {
      // Get current user authentification
      const user = await Auth.currentAuthenticatedUser();
      // Send to aws the MFA code that he receive in SMS and verified if it's the good code that was send to his phone number
      await Auth.verifyUserAttributeSubmit(user, 'phone_number', codeMFA);
      // If the verifyUserAttributeSubmit work it will automatically activate this user to be authentificated by MFA when he log-in
      await Auth.setPreferredMFA(user, 'SMS_MFA');
      // Trigger a function that will display a message t the user on success
      this.MFASignUpSuccess.next();
    } catch (error) {
      // trigger a function in lgin component to display a message ribon to the user
      this.wrongMFACode.next();
      this.checkForbidden(error);
      console.error('Error in the confirmation of the user: ' + error);
    }
  }

  // Function called  in the sign-up MFA window when user click submit and try to verified if the TOTP token work between his authenticate app and cognito
  async singUpMFACodeTOTP(MFACode: string) {
    try {
      // Get the current CognitoUser
      const user = await Auth.currentAuthenticatedUser();

      // Verify the TOTP token that user enter if cognito can see it
      await Auth.verifyTotpToken(user, MFACode)
        .then((confirmation) => {
          this.confirmMFAMessage = 'mfaSignUpTOTPSuccess';
          // If the verified token is good, confirmation will be success and we'll set the MFA preference of the user to SOFTWARE_TOKEN_MFA
          Auth.setPreferredMFA(user, 'SOFTWARE_TOKEN_MFA');
          // Redirect the user to the user-edit page
          // this.router.navigate(['/user-edit']);
          // Trigger a function that will display a message t the user on success
          this.MFASignUpSuccess.next();
        })
        .catch((error) => {
          this.checkForbidden(error);
          console.error(error);
          // Trigger a function in the login component to display a message ribon to the user
          this.wrongMFACode.next();
        });
    } catch (error) {
      this.checkForbidden(error);
      console.error(error);
    }
  }

  // Send a new email validation code to the current user
  async resendEmailValidationCode(): Promise<boolean> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.verifyUserAttribute(user, 'email');
      return true;
    } catch (error) {
      this.checkForbidden(error);
      console.error(error);
      return false;
    }
  }

  // Function that send a SMS to the user so he will be able to activate the SMS_MFA
  /////////////////// TODO: will need to be continued when we get access to SMS from cognito /////////////////////
  async sendSMS() {
    try {
      // Set the instance of the user
      const user = await Auth.currentAuthenticatedUser();
      // Send a SMS to the phone number of the user that is provide by cognito
      await Auth.verifyUserAttribute(user, 'phone_number');
    } catch (error) {
      this.checkForbidden(error);
      console.error('Error on sending SMS: ' + error);
    }
  }

  // Function that get a QR code
  async getQrCode() {
    Auth.currentAuthenticatedUser()
      .then(async (user) => {
        // Call the function to get a secret code from cognito and put it into qrCode
        const qrCode = await Auth.setupTOTP(user);
        // Set the url string to get a readable QR code from lib qrcode
        const qrCodeUrl =
          'otpauth://totp/' +
          user.username +
          '?secret=' +
          qrCode +
          // '&issuer=IFM&algorithm=SHA1&digits=6&period=30';
          '&issuer=NOVA&algorithm=SHA1&digits=6&period=30';

        // Call function toDataUrl from lib qrcode to get an imageUrl of the qr code readable for the user
        qrcode
          .toDataURL(qrCodeUrl)
          .then((imageUrl: any) => {
            this.qrCodeImgActive = true;
            // Set the result in a variable that will be used in HTML to display the Qr code to the user
            this.imgUrl = imageUrl;
          })
          .catch((error: any) => {
            console.error(error);
          });
      })
      .catch((error) => {
        console.error(error);
        this.checkForbidden(error);
      });
  }

  // Get user MFA preference
  async getUserMFAPreference() {
    // Get the instence of the user
    await Auth.currentAuthenticatedUser().then((cognitoUser) => {
      // Get user MFA preference if this user is allready set
      Auth.getPreferredMFA(cognitoUser, { bypassCache: true })
        .then((result) => {
          this.userMFAPreference = result; // Set variable to the preference of MFA of the user
        })
        .catch((err) => {
          this.checkForbidden(err);
          console.error(err);
        });
    });
  }

  // Adds User to Cognito User Group. Called during User creation.
  async addUserToGroup(username: string, groupName: string) {
    try {
      //Declare Cognito Identity Provider class
      const cognitoIdentityServiceProvider = this.getCognitoClient();

      //Set reqiored parameters for the API call
      const params = {
        UserPoolId: environment.cognito.userPoolId,
        Username: username,
        GroupName: groupName,
      };

      // Call adminAddUserToGroup API to add the user to the group
      await cognitoIdentityServiceProvider.send(
        new AdminAddUserToGroupCommand(params)
      );
    } catch (error) {
      //log error in console
      this.checkForbidden(error);
      console.error('Error adding user to group:', error);
    }
  }

  async federatedSignIn(
    ambiente: any,
    accessToken: string,
    expires: number,
    name: string,
    email: string
  ) {
    return Auth.federatedSignIn(
      //  "sts.windows.net/c0b50357-f018-4f60-bf08-47342010bf9b"
      ambiente,
      // 'sts.windows.net/c0b50357-f018-4f60-bf08-47342010bf9b/',
      {
        token: accessToken,
        expires_at: expires,
      },
      {
        name: name,
        email: email,
      }
    );
  }

  async checkForbidden(error: any) {
    this.verifySession();
    // Your asynchronous operation that may result in a 403 error
    if (
      error.toString().includes('Forbidden') ||
      error.toString().includes('Expired')
    ) {
      // Handle 403 error here
      await Auth.signOut().then(() => {
        let theme = localStorage.getItem('theme') ?? 'light';
        theme = theme?.replaceAll(/["\\]/g, '') ?? 'light';
        // Clear the local storage
        localStorage.clear();
        sessionStorage.clear();
        localStorage.setItem('theme', '"' + theme + '"' || 'light');
        if (this.router.url !== '/login') {
          this.router.navigate(['/login']);
        }
      });
    } else {
      this.configure();
      // Handle other errors
      console.error('An error occurred:', error);
    }
  }
}
